import PropTypes from 'prop-types';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import metricsReference from '../../../../Patient/Extras/spyderchart_metrics.json';
import './Progress.scss';

const Progress = ({ userData, selectedTest }) => {
  if (!userData || !userData.testHistory?.length) {
    return <p className="no-data">No test data available for chart.</p>;
  }

  const tests = userData.testHistory;
  const hasMultipleTests = tests.length > 1;
  let currentTest, previousTest;
  if (selectedTest) {
    const selectedIndex = tests.findIndex((t) => t.date === selectedTest.date && t.overallScore === selectedTest.overallScore);
    if (selectedIndex >= 0) {
      currentTest = tests[selectedIndex];
      previousTest = selectedIndex > 0 ? tests[selectedIndex - 1] : null;
    } else {
      currentTest = tests[tests.length - 1];
      previousTest = hasMultipleTests ? tests[tests.length - 2] : null;
    }
  } else {
    currentTest = tests[tests.length - 1];
    previousTest = hasMultipleTests ? tests[tests.length - 2] : null;
  }
  // 🔍 Extract metric values from API
  const normalizeKey = (str) => str.toLowerCase().replace(/[_\s]/g, '');

  const getMetricValue = (test, metricKey) => {
    const target = normalizeKey(metricKey);

    for (const categoryKey in test.categories) {
      const category = test.categories[categoryKey];
      if (!category?.metrics) continue;

      for (const m of category.metrics) {
        if (normalizeKey(m.name) === target) {
          return Number(m.value) || 0;
        }
      }
    }

    return 0;
  };

  // Helper: find metric by category and possible names
  const findByNames = (test, categoryKey, names) => {
    const metrics = test?.categories?.[categoryKey]?.metrics || [];
    const found = metrics.find((m) => names.some((n) => m.name === n));
    return found ? Number(found.value) || 0 : 0;
  };

  // Pull inputs per test
  const extractInputs = (test) => {
    if (!test) return null;
    const volume = findByNames(test, 'concentration', ['Volume']);
    const concentration = findByNames(test, 'concentration', ['Concentration']);
    const pr = findByNames(test, 'motility', ['Progressive motility']);
    const rapid = findByNames(test, 'motility', ['Rapid progressive']);
    const morphology = findByNames(test, 'morphology', ['Normal forms']);
    // DFI from dnaIntegrity category if present
    const dfi = findByNames(test, 'dnaIntegrity', ['DNA fragmentation', 'DNA Fragmentation', 'DFI']);
    const hasDFI = dfi > 0; // treat 0 as missing

    return { volume, concentration, pr, rapid, morphology, dfi: hasDFI ? dfi : null };
  };

  // Score calculators (0–100 clamped)
  const clamp01 = (x) => Math.min(100, Math.max(0, x));

  const scoreFor = (metricKey, { volume, concentration, pr, rapid, morphology, dfi }) => {
    switch (metricKey) {
      case 'progressive_motile_sperm': {
        const tmsc = volume * concentration * (pr / 100);
        const score = ((tmsc - 5) / 95) * 100; // 5..100M → 0..100
        return clamp01(score);
      }
      case 'progressive_motility': {
        const score = ((pr - 20) / 55) * 100; // 20..75 → 0..100
        return clamp01(score);
      }
      case 'rapid_progressive': {
        const score = ((rapid - 10) / 40) * 100; // 10..50 → 0..100
        return clamp01(score);
      }
      case 'normal_forms': {
        const score = ((morphology - 2) / 8) * 100; // 2..10 → 0..100
        return clamp01(score);
      }
      case 'concentration': {
        const score = ((concentration - 10) / 50) * 100; // 10..60 → 0..100
        return clamp01(score);
      }
      case 'dna_fragmentation': {
        if (dfi == null) return null; // skip if not provided
        // Lower is better: 40..15 → 10..100 (per reference)
        const score = ((40 - dfi) / 25) * 100;
        // enforce floor 10 like reference caps
        return Math.max(10, clamp01(score));
      }
      default:
        return 0;
    }
  };

  const inputsCurrent = extractInputs(currentTest);
  const inputsPrevious = previousTest ? extractInputs(previousTest) : null;

  // 🧮 Build chart data with custom scoring
  const data = metricsReference
    .map((metric) => {
      const key = metric.metricKey;

      // Compute raw values per axis for tooltip
      let currentRaw = 0;
      let previousRaw = 0;
      let unit = '%';

      switch (key) {
        case 'progressive_motile_sperm': {
          const tmscCurr = inputsCurrent.volume * inputsCurrent.concentration * (inputsCurrent.pr / 100);
          currentRaw = Number(tmscCurr.toFixed(2));
          const tmscPrev = inputsPrevious ? inputsPrevious.volume * inputsPrevious.concentration * (inputsPrevious.pr / 100) : 0;
          previousRaw = Number(tmscPrev.toFixed(2));
          unit = 'M';
          break;
        }
        case 'progressive_motility': {
          currentRaw = inputsCurrent.pr;
          previousRaw = inputsPrevious ? inputsPrevious.pr : 0;
          unit = '%';
          break;
        }
        case 'rapid_progressive': {
          currentRaw = inputsCurrent.rapid;
          previousRaw = inputsPrevious ? inputsPrevious.rapid : 0;
          unit = '%';
          break;
        }
        case 'normal_forms': {
          currentRaw = inputsCurrent.morphology;
          previousRaw = inputsPrevious ? inputsPrevious.morphology : 0;
          unit = '%';
          break;
        }
        case 'concentration': {
          currentRaw = inputsCurrent.concentration;
          previousRaw = inputsPrevious ? inputsPrevious.concentration : 0;
          unit = 'M';
          break;
        }
        case 'dna_fragmentation': {
          currentRaw = inputsCurrent.dfi ?? 0;
          previousRaw = inputsPrevious && inputsPrevious.dfi != null ? inputsPrevious.dfi : 0;
          unit = '%';
          break;
        }
        default:
          // fallback to original lookup by metric name if present
          currentRaw = getMetricValue(currentTest, key);
          previousRaw = previousTest ? getMetricValue(previousTest, key) : 0;
      }

      // Skip if current score is not computable (e.g., DFI missing)
      const currentScore = scoreFor(key, inputsCurrent);
      if (currentScore == null) return null;

      // Scores
      const previousScore = inputsPrevious ? scoreFor(key, inputsPrevious) : 0;

      // WHO baseline scored using same formula when possible
      const whoMin = metric.whoMinimumScore;
      let whoNorm = 0;
      if (typeof whoMin === 'number') {
        const pseudoInputs = {
          ...inputsCurrent,
          // override the specific raw for the axis
          pr: key === 'progressive_motility' ? whoMin : inputsCurrent.pr,
          rapid: key === 'rapid_progressive' ? whoMin : inputsCurrent.rapid,
          morphology: key === 'normal_forms' ? whoMin : inputsCurrent.morphology,
          concentration: key === 'concentration' ? whoMin : inputsCurrent.concentration,
          dfi: key === 'dna_fragmentation' ? whoMin : inputsCurrent.dfi,
        };
        if (key === 'progressive_motile_sperm') {
          // For TMSC WHO, approximate by using PR=whoMin while keeping vol/conc from current
          pseudoInputs.pr = whoMin;
        }
        const s = scoreFor(key, pseudoInputs);
        whoNorm = typeof s === 'number' ? s : 0;
      }

      return {
        metric: metric.uiName,
        current: currentScore,
        previous: previousScore,
        whoNorm,
        currentRaw,
        previousRaw,
        whoCutoff: whoMin,
        chartRange: metric.chartRange,
        unit,
      };
    })
    .filter(Boolean);

  const currentDate = new Date(currentTest.date).toLocaleDateString('en-US');
  const previousDate = previousTest ? new Date(previousTest.date).toLocaleDateString('en-US') : null;

  return (
    <div className="progress-container dash-card">
      <div className="progress-header">
        <p className="progress-subtitle">Track your fertility performance across all key metrics</p>
      </div>

      <div className="progress-chart" style={{ width: '100%', height: 350 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="80%">
            <PolarGrid stroke="rgba(255, 255, 255, 0.08)" fill="rgba(0, 0, 0, 0.1)" strokeDasharray="3 3" />
            <PolarAngleAxis
              dataKey="metric"
              tick={{
                fill: 'var(--muted-text)',
                fontSize: 11,
                fontWeight: 400,
              }}
            />

            <Tooltip
              content={({ payload }) => {
                if (!payload || !payload.length) return null;
                const item = payload[0].payload;
                return (
                  <div
                    style={{
                      background: '#011241',
                      borderRadius: 4,
                      padding: '6px 10px',
                      fontSize: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,.06)',
                      color: 'var(--muted-text)',
                    }}
                  >
                    <strong>{item.metric}</strong>
                    <br />
                    <span>Score: {Number(item.current).toFixed(2)}</span>
                    {typeof item.whoCutoff === 'number' && (
                      <>
                        <br />
                        <span>
                          WHO Cutoff: {item.whoCutoff}
                          {item.unit}
                        </span>
                      </>
                    )}
                  </div>
                );
              }}
            />

            {/* WHO baseline (scaled) */}
            <Radar name="WHO Range" dataKey="whoNorm" stroke="#EEF3FC" fill="#ef4444" fillOpacity={0.08} strokeWidth={2} strokeDasharray="4 4" />

            {/* Previous test layer */}
            {hasMultipleTests && (
              <Radar name={`Previous (${previousDate})`} dataKey="previous" stroke="#64748b" fill="#64748b" fillOpacity={0.15} strokeWidth={2} />
            )}

            {/* Current test layer */}
            <Radar name={`Current (${currentDate})`} dataKey="current" stroke="#0048FF" fill="rgba(0, 72, 255, 0.2)" strokeWidth={3} />
            {/* <Legend wrapperStyle={{ paddingTop: '10px', fontSize: 12 }} iconType="line" /> */}
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="color-indicator-block">
        <p className="white">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 7.64014H14" stroke="white" strokeWidth="3" />
          </svg>
          WHO Normal Threshold (60)
        </p>
        <p className="blue">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 7.64014H14" stroke="currentColor" strokeWidth="3" />
          </svg>
          Current: Jul 15, 2025
        </p>
        <p className="grey">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.5 7.64014H14" stroke="currentColor" strokeWidth="3" />
          </svg>
          Previous: Oct 15, 2025
        </p>
      </div>

      {/* <p className="progress-note" style={{ textAlign: 'center', marginTop: 10 }}>
        💡 <strong>Tip:</strong> Each spoke’s length is scaled to its actual clinical chart range from the WHO reference data. Higher = better.
      </p>

      {!hasMultipleTests && <p className="progress-note">Complete your next test to see progress trends.</p>} */}
    </div>
  );
};

Progress.propTypes = {
  userData: PropTypes.shape({
    testHistory: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        categories: PropTypes.object.isRequired,
      })
    ),
  }).isRequired,
  selectedTest: PropTypes.shape({
    date: PropTypes.string,
    overallScore: PropTypes.number,
  }),
};

export default Progress;
